import { useCallback, useEffect, useState } from "react";
import { api, resolveMediaUrl } from "../../lib/api";
import { Card } from "../ui/card";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { GalleryItem } from "./gallery/GalleryItem";
import { useDragReorder } from "../../hooks/useDragReorder";

const CATEGORIES = [
  { key: "bodas", label: "Bodas" },
  { key: "xv", label: "XV Años" },
  { key: "sesiones", label: "Sesiones de Fotos" },
  { key: "foto_video", label: "Foto + Video" },
  { key: "save_the_date", label: "Save the Date" },
];

const resolveUrl = resolveMediaUrl;

export const AdminGallery = () => {
  const [gallery, setGallery] = useState([]);
  const [category, setCategory] = useState("bodas");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => api.get("/gallery").then((r) => setGallery(r.data)), []);

  useEffect(() => {
    load();
  }, [load]);

  const photos = gallery.filter((g) => g.category === category);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", category);
      await api.post("/admin/photos", formData);
      toast.success("Foto subida correctamente");
      await load();
    } catch {
      toast.error("Error al subir la foto");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = async (id) => {
    try {
      await api.delete(`/admin/photos/${id}`);
      toast.success("Foto eliminada");
      await load();
    } catch {
      toast.error("Error al eliminar la foto");
    }
  };

  const { onDragStart, onDragOver, onDrop } = useDragReorder({
    filteredItems: photos,
    setAllItems: setGallery,
    filterKey: "category",
    filterValue: category,
    reorderRequest: (orderedIds) => api.put("/admin/photos/reorder", { category, ordered_ids: orderedIds }),
    reload: load,
  });

  return (
    <Card className="p-6 space-y-6" data-testid="admin-gallery-panel">
      <h3 className="font-semibold">Galería de Fotos</h3>
      <p className="text-sm text-muted-foreground">
        Sube, elimina y reordena (arrastra) tus fotos. Aparecerán automáticamente en la galería pública.
      </p>

      <Tabs value={category} onValueChange={setCategory}>
        <TabsList data-testid="admin-gallery-category-tabs" className="flex-wrap h-auto">
          {CATEGORIES.map((c) => (
            <TabsTrigger key={c.key} value={c.key} data-testid={`admin-gallery-tab-${c.key}`}>
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <label
        data-testid="admin-gallery-upload-label"
        className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 cursor-pointer hover:bg-secondary/50 transition-colors"
      >
        <Upload size={18} />
        <span className="text-sm">{uploading ? "Subiendo..." : `Subir foto a "${CATEGORIES.find((c) => c.key === category)?.label}"`}</span>
        <input data-testid="admin-gallery-upload-input" type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
      </label>

      {photos.length === 0 ? (
        <p data-testid="admin-gallery-empty" className="text-sm text-muted-foreground">Aún no hay fotos en esta categoría.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {photos.map((img, idx) => (
            <GalleryItem
              key={img.id}
              img={img}
              idx={idx}
              resolveUrl={resolveUrl}
              onDragStart={() => onDragStart(idx)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(idx)}
              onRemove={() => removePhoto(img.id)}
            />
          ))}
        </div>
      )}
    </Card>
  );
};
